import { Button, InputLabel, Typography } from "@suraasa/placebo-ui"
import { useQueryClient } from "@tanstack/react-query"
import api from "api"
import { LeadProfile } from "api/resources/lead/types"
import { Plus } from "iconoir-react"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { convertToAnnual, formatNumberWithCommas } from "utils/helpers"

import AutoSaveSelect, { formatSelectValue } from "./AutoSaveSelect"
import AutoSaveTextArea from "./AutoSaveTextArea"
import AutoSaveTextField from "./AutoSaveTextField"
import { useUserLeadContext } from "./Context"
import StepBar from "./StepBar"
import {
  outcomeBucketChoices,
  OutcomeBucketValues,
  targetCountryChoices,
} from "./utils/types"

const Step2 = () => {
  const { userId } = useParams()
  const { leadProfile } = useUserLeadContext()

  const { desiredOutcome, primaryGoal, desiredCountries, secondaryGoal, id } =
    leadProfile || {}

  const [currentSalary, setCurrentSalary] = useState(
    leadProfile ? Number(leadProfile.currentSalary) / 12 : ""
  )
  const [currentSalaryCurrency, setCurrentSalaryCurrency] = useState(
    leadProfile ? leadProfile.currentSalaryCurrency : ""
  )
  const [desiredSalary, setDesiredSalary] = useState(
    leadProfile ? Number(leadProfile.desiredSalary) / 12 : ""
  )
  const [desiredSalaryCurrency, setDesiredSalaryCurrency] = useState(
    leadProfile ? leadProfile.desiredSalaryCurrency : ""
  )

  const [currencies, setCurrencies] = useState<
    { label: string; value: string }[]
  >([])

  const [showSecondaryGoal, setShowSecondaryGoal] = useState(
    Boolean(secondaryGoal)
  )

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const listCurrencies = await api.global.currency.listCurrencies({
          params: { page: -1 },
        })
        setCurrencies(
          listCurrencies.map(c => ({
            value: c.code,
            label: `${c.code} (${c.symbol})`,
          }))
        )
      } catch (e) {
        // console.error(e)
      }
    }
    fetchCurrencies()
  }, [])

  const queryClient = useQueryClient()

  const updateSalaryData = async (data: any) => {
    try {
      await api.lead.saveData({
        data,
        params: {
          userPrimaryKey: id,
        },
      })

      queryClient.setQueryData<LeadProfile>(["profile", userId], oldData => ({
        ...(oldData ?? {}),
        ...data,
      }))
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }

  const isTeachAbroad = desiredOutcome === OutcomeBucketValues.TEACH_ABROAD
  return (
    <>
      <StepBar
        number={2}
        text="Understand your dreams and goals"
        className="my-2"
      />
      <div className="flex flex-col gap-2">
        <div className="rounded-2xl border border-onSurface-200 bg-white p-3">
          <div className="flex flex-col gap-2">
            <div className="max-w-md">
              <AutoSaveSelect
                label="Outcome Bucket"
                placeholder="Ex - Start Teaching"
                saveKey="desiredOutcome"
                value={formatSelectValue(desiredOutcome)}
                options={outcomeBucketChoices}
                userPrimaryKey={id}
              />
            </div>
            {isTeachAbroad && (
              <div className="max-w-md">
                <AutoSaveSelect
                  createable
                  label="Target Country for Abroad"
                  placeholder="Ex - UAE, USA"
                  saveKey="desiredCountries"
                  value={formatSelectValue(desiredCountries)}
                  options={targetCountryChoices}
                  userPrimaryKey={id}
                  isMulti
                />
              </div>
            )}
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="w-full max-w-md">
                <InputLabel label="Current Monthly Salary" />
                <div className="flex">
                  <AutoSaveSelect
                    label=""
                    placeholder="INR (₹)"
                    saveKey="currentSalaryCurrency"
                    value={formatSelectValue(currentSalaryCurrency)}
                    userPrimaryKey={id}
                    options={currencies}
                    onUpdate={v => {
                      if (v && !Array.isArray(v)) {
                        setCurrentSalaryCurrency(v.value as string)
                        updateSalaryData({
                          currentSalary: convertToAnnual(Number(currentSalary)),
                          currentSalaryCurrency: v.value,
                        })
                      }
                    }}
                    disableAutoSave
                    className="!w-[200px] border-r-0 [&_div]:!rounded-r-none"
                  />
                  <AutoSaveTextField
                    label=""
                    placeholder="Ex - 1,00,000"
                    saveKey="currentSalary"
                    userPrimaryKey={id}
                    value={currentSalary ?? undefined}
                    onUpdate={v => {
                      setCurrentSalary(v)
                      updateSalaryData({
                        currentSalary: v * 12,
                        currentSalaryCurrency: currentSalaryCurrency,
                      })
                    }}
                    disableAutoSave
                    className="[&>div]:rounded-l-none [&>div]:border-l-0"
                  />
                </div>
                <div className="h-0.5"></div>
                <Typography variant="smallBody" className="text-onSurface-500">
                  {currentSalaryCurrency}{" "}
                  {formatNumberWithCommas(
                    convertToAnnual(Number(currentSalary))
                  )}{" "}
                  Annually
                </Typography>
              </div>
              <div className="w-full max-w-md">
                <InputLabel label="Target Monthly Salary" />
                <div className="flex">
                  <AutoSaveSelect
                    label=""
                    placeholder="INR (₹)"
                    saveKey="desiredSalaryCurrency"
                    value={formatSelectValue(desiredSalaryCurrency)}
                    options={currencies}
                    userPrimaryKey={id}
                    onUpdate={v => {
                      if (v && !Array.isArray(v)) {
                        setDesiredSalaryCurrency(v.value as string)
                        updateSalaryData({
                          desired_salary: Number(desiredSalary) * 12,
                          desired_salary_currency: v.value,
                        })
                      }
                    }}
                    disableAutoSave
                    className="!w-[200px] border-r-0 [&_div]:!rounded-r-none"
                  />
                  <AutoSaveTextField
                    label=""
                    placeholder="Ex - 1,00,000"
                    saveKey="desiredSalary"
                    userPrimaryKey={id}
                    value={desiredSalary ?? undefined}
                    onUpdate={v => {
                      setDesiredSalary(v)
                      updateSalaryData({
                        desired_salary: v * 12,
                        desired_salary_currency: desiredSalaryCurrency,
                      })
                    }}
                    disableAutoSave
                    className="[&>div]:rounded-l-none [&>div]:border-l-0"
                  />
                </div>
                <div className="h-0.5"></div>
                <Typography variant="smallBody" className="text-onSurface-500">
                  {desiredSalaryCurrency}{" "}
                  {formatNumberWithCommas(Number(desiredSalary) * 12)} Annually
                </Typography>
              </div>
            </div>
            <div>
              <AutoSaveTextArea
                label="Primary Goal"
                placeholder="Ex - Want to teach in USA"
                saveKey="primaryGoal"
                value={primaryGoal ?? undefined}
                userPrimaryKey={id}
                rows={3}
              />
            </div>
            <div className="items-start">
              {!showSecondaryGoal && (
                <Button
                  variant="text"
                  label={"Add Secondary Goal"}
                  startAdornment={<Plus className="!size-3"></Plus>}
                  onClick={() => setShowSecondaryGoal(prev => !prev)}
                ></Button>
              )}
            </div>
            {showSecondaryGoal && (
              <div>
                <AutoSaveTextArea
                  label="Secondary Goal"
                  placeholder="Ex - Want to teach outside of India"
                  saveKey="secondaryGoal"
                  value={secondaryGoal ?? undefined}
                  userPrimaryKey={id}
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Step2
